/**
 * f14.3: step 7's reads and writes, over TanStack Query (adr-14).
 *
 * Every key sits under `['cycle', id, ...]`: the step list and the f14.1
 * batch reads under `actuals-step`, the batch's variance summary under the
 * f14.2 queue prefix so a decision in the queue refetches it. Capabilities
 * and every figure are the server's.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import { isJobTerminal, JOB_POLL_INTERVAL_MS } from '@/api/queries';
import { queryKeys } from '@/api/queryKeys';

const PARSING_STATES: readonly string[] = ['RECEIVED', 'PARSING'];

export function useActualsStepBatches(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.actualsStep(cycleId),
    queryFn: () => api.listActualsStepBatches(cycleId),
    refetchInterval: (query) =>
      query.state.data?.batches.some((batch) => PARSING_STATES.includes(batch.state.code))
        ? JOB_POLL_INTERVAL_MS
        : false,
  });
}

export function useActualsReview(cycleId: string, batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.actualsBatch(cycleId, batchId ?? ''),
    queryFn: () => api.getActualsBatch(batchId as string),
    enabled: Boolean(batchId),
  });
}

export function useActualsRows(cycleId: string, batchId: string, matchStatus: string) {
  return useQuery({
    queryKey: queryKeys.cycle.actualsBatchRows(cycleId, batchId, matchStatus),
    queryFn: () => api.listActualsBatchRows(batchId, matchStatus),
    placeholderData: (previous) => previous,
  });
}

/** A step-7 parse or commit job, polled until terminal. */
export function useActualsStepJob(cycleId: string, jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.actualsStepJob(cycleId, jobId ?? ''),
    queryFn: () => api.getActualsStepJob(cycleId, jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) => (isJobTerminal(query.state.data?.state) ? false : JOB_POLL_INTERVAL_MS),
    staleTime: 0,
    retry: 2,
  });
}

/** The variances one committed batch raised (f14.2 `summary?batch_id=`). */
export function useBatchVarianceSummary(cycleId: string, batchId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.reconciliationBatchSummary(cycleId, batchId),
    queryFn: () => api.getReconciliationSummary(cycleId, batchId),
    enabled: Boolean(batchId),
    retry: false,
  });
}

/** The pending total per currency of one batch (the queue's own list body, one row asked for). */
export function useBatchPendingTotals(cycleId: string, batchId: string) {
  const query = { batch_id: batchId, status: 'PENDING', page_size: 1 };
  return useQuery({
    queryKey: queryKeys.cycle.reconciliationList(cycleId, query),
    queryFn: () => api.listReconciliationVariances(cycleId, query),
    retry: false,
  });
}

function invalidateStep(queryClient: QueryClient, cycleId: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.actualsStep(cycleId) });
}

export function useUploadActualsStepBatch(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadActualsStepBatch(cycleId, file),
    onSuccess: () => {
      invalidateStep(queryClient, cycleId);
      // The upload records activity on step 7 (PENDING -> IN_PROGRESS).
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.workflow(cycleId) });
    },
  });
}

export function useCommitActualsBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.commitActualsBatch(batchId),
    onSettled: () => invalidateStep(queryClient, cycleId),
  });
}

export function useDiscardActualsBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.discardActualsBatch(batchId),
    onSettled: () => invalidateStep(queryClient, cycleId),
  });
}

/** After a parse: the step. After a commit: the whole cycle (rail, queue, figures). */
export function useRefreshActualsStep(cycleId: string) {
  const queryClient = useQueryClient();
  return (committed: boolean) => {
    invalidateStep(queryClient, cycleId);
    if (committed) void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.detail(cycleId) });
  };
}
